import type { DealerTheme } from "conversion_flows/shared/contexts/dealerTheme"
import themeTokens from "conversion_flows/theme/LibUIThemeTokens"
/**
 * Fontsource provides an advanced import mode
 * TODO: Switch to advanced mode when webpack is replaced by vite
 */
import '@fontsource/poppins/600.css';

/**
 * The function returns a CSS style string containing
 * CSS Custom Properties that mirror the `@autouncle/ui` library
 * tokens (and more specifically, the tokens in
 * `conversion_flows/theme/LibUIThemeProvider.tsx`). They have
 * an added `au-` prefix to prevent clashes with CSS Custom
 * Properties on dealer's websites. If a token you need is
 * missing, please add it. Do not add unnecessary tokens, as
 * the string is meant to be inlined in (potentially) multiple
 * locations of the same page, as needed.
 *
 * The tokens should be used to style `.svelte` components
 * consistently, according to the dealer's theme settings.
 */

export function getDealerThemeTokensStyle(dealerTheme: DealerTheme) {
  const tokens = themeTokens(dealerTheme)

  const buttonBorderRadius = dealerTheme.buttonBorderRadius ?? "sm"

  return [
    `--au-logo-color: ${tokens("colors.logo")}`,

    `--au-background-default: ${tokens("colors.white")}`,
    `--au-background-neutral: ${tokens("colors.primary.50")}`,
    `--au-background-neutral-subtle: ${tokens("colors.primary.100")}`,
    `--au-background-negative-subtle: ${tokens("colors.error.100")}`,
    `--au-background-information-subtle: ${tokens("colors.attention.200")}`,
    `--au-background-positive-subtle: ${tokens("colors.success.200")}`,

    `--au-text-heading-2-font-size: ${tokens("fontSize.base")}`,
    `--au-text-heading-2-line-height: ${tokens("lineHeight.base")}`,

    // button primary
    `--au-button-primary-background-color: ${tokens("colors.primary.500")}`,
    `--au-button-primary-hover-background-color: ${tokens(
      "colors.primary.400"
    )}`,
    `--au-button-primary-active-background-color: ${tokens(
      "colors.primary.600"
    )}`,
    `--au-button-primary-border-color: ${tokens("colors.primary.500")}`,
    `--au-button-primary-hover-border-color: ${tokens("colors.primary.400")}`,
    `--au-button-primary-active-border-color: ${tokens("colors.primary.600")}`,
    `--au-button-primary-color: ${tokens("colors.primaryContrastForeground")}`,
    `--au-button-primary-hover-color: ${tokens("colors.primaryContrastForeground")}`,
    `--au-button-primary-icon-color: ${tokens("colors.primaryContrastForeground")}`,
    `--au-button-primary-hover-icon-color: ${tokens("colors.primaryContrastForeground")}`,

    // button secondary
    `--au-button-secondary-background-color: ${tokens("colors.white")}`,
    `--au-button-secondary-hover-background-color: ${tokens("colors.primaryHoverBackground")}`,
    `--au-button-icon-secondary-hover-background-color: ${tokens(
      "colors.primary.50"
    )}`, // secondary ButtonIcon needs a hover effect
    `--au-button-secondary-active-background-color: ${tokens(
      "colors.primary.100"
    )}`,
    `--au-button-secondary-border-color: ${tokens("colors.primary.350")}`,
    `--au-button-secondary-hover-border-color: ${tokens("colors.primaryForegroundColor")}`,
    `--au-button-secondary-active-border-color: ${tokens(
      "colors.primaryForegroundColor"
    )}`,
    `--au-button-secondary-color: ${tokens("colors.grey.150")}`,
    `--au-button-secondary-hover-color: ${tokens("colors.primaryForegroundColor")}`,
    `--au-button-secondary-icon-color: ${tokens("colors.primaryForegroundColor")}`,
    `--au-button-secondary-hover-icon-color: ${tokens("colors.primaryForegroundColor")}`,

    // button link
    `--au-button-link-background-color: transparent`,
    `--au-button-link-hover-background-color: transparent`,
    `--au-button-link-active-background-color: transparent`,
    `--au-button-link-border-color: transparent`,
    `--au-button-link-active-border-color: transparent`,
    `--au-button-link-hover-border-color: transparent`,
    `--au-button-link-color: ${tokens("colors.primary.500")}`,
    `--au-button-link-hover-color: ${tokens("colors.primary.600")}`,
    `--au-button-link-icon-color: ${tokens("colors.primary.500")}`,
    `--au-button-link-hover-icon-color: ${tokens("colors.primary.600")}`,

    `--au-button-default-shadow: ${tokens("shadow.offset.bottom")} ${tokens(
      "shadow.blurRadius.100"
    )} ${tokens("shadow.color.default")}`,
    `--au-button-border-radius: ${tokens(`borderRadius.${buttonBorderRadius}`)}`,

    `--au-focus-outline-color: ${tokens("colors.primary.300")}`,
    `--au-focus-outline-width: ${tokens("spacing.3xs")}`,

    `--au-text-button-font-size-base: ${tokens("fontSize.sm")}`,
    `--au-text-button-line-height-base: ${tokens("lineHeight.sm")}`,

    /**
     * Usage of maximum values here is due to the context
     * in which these components are rendered (they appear on
     * the dealer's website where we do not have control over
     * `z-index`es)
     */
    `--au-z-index-widget: 2147483645`,
    `--au-z-index-backdrop: 2147483646`,
    `--au-z-index-modals: 2147483647` /* max value */,

    `--au-backdrop-background-color: ${tokens("colors.black")}`,
    `--au-backdrop-opacity: 0.6`,

    `--au-disabled-opacity: ${tokens("disabled.opacity")}`,

    `--au-border-radius-default: ${tokens("borderRadius.md")}`,
    `--au-border-radius-sharp: ${tokens("borderRadius.sm")}`,

    `--au-box-shadow-weak: ${tokens("boxShadow.base")}`,
    `--au-box-shadow-default: ${tokens("boxShadow.100")}`,
    `--au-box-shadow-strong: ${tokens("boxShadow.300")}`,

    `--au-box-default-box-shadow: ${tokens("boxShadow.100")}`,

    `--au-spacing-3xs: ${tokens("spacing.3xs")}`,
    `--au-spacing-2xs: ${tokens("spacing.2xs")}`,
    `--au-spacing-xs: ${tokens("spacing.xs")}`,
    `--au-spacing-sm: ${tokens("spacing.sm")}`,
    `--au-spacing-md: ${tokens("spacing.md")}`,
    `--au-spacing-2md: ${tokens("spacing.2md")}`,
    `--au-spacing-lg: ${tokens("spacing.lg")}`,
    `--au-spacing-xl: ${tokens("spacing.xl")}`,
    `--au-spacing-2xl: ${tokens("spacing.2xl")}`,

    `--au-tappable-size-sm: ${tokens("spacing.xl")}`,
    `--au-tappable-size: ${tokens("spacing.2xl")}`,
    `--au-tappable-size-lg: ${tokens("spacing.10")}`,

    `--au-line-width-default: 1px`,

    `--au-text-font-family-primary: ${tokens("fontFamily.body")}`,
    `--au-text-font-family-secondary: ${dealerTheme.customFontFamily ?? tokens("fontFamily.body")}`,

    `--au-text-color-body: ${tokens("colors.grey.100")}`,
    `--au-text-color-link: ${tokens("colors.primaryForegroundColor")}`,
    `--au-text-color-default: ${tokens("colors.grey.200")}`,
    `--au-text-color-information: ${tokens("colors.attention.700")}`,
    `--au-text-color-positive: ${tokens("colors.success.700")}`,
    `--au-text-color-negative: ${tokens("colors.error.700")}`,

    `--au-line-color-neutral: ${tokens("colors.primary.200")}`,
    `--au-line-color-information: ${tokens("colors.attention.600")}`,
    `--au-line-color-positive: ${tokens("colors.success.500")}`,
    `--au-line-color-negative: ${tokens("colors.error.200")}`,

    `--au-text-heading-1-font-size: ${tokens("fontSize.xl")}`,
    `--au-text-heading-1-line-height: ${tokens("lineHeight.xl")}`,

    `--au-text-heading-2-font-size: ${tokens("fontSize.lg")}`,
    `--au-text-heading-2-line-height: ${tokens("lineHeight.lg")}`,

    `--au-text-body-base-font-size: ${tokens("fontSize.base")}`,
    `--au-text-body-base-line-height: ${tokens("lineHeight.base")}`,

    `--au-text-body-small-font-size: ${tokens("fontSize.sm")}`,
    `--au-text-body-small-line-height: ${tokens("lineHeight.sm")}`,

    `--au-text-body-xsmall-font-size: ${tokens("fontSize.xs")}`,
    `--au-text-body-xsmall-line-height: ${tokens("lineHeight.xs")}`,

    `--au-text-weight-regular: ${tokens("fontWeight.regular")}`,
    `--au-text-weight-medium: ${tokens("fontWeight.medium")}`,
    `--au-text-weight-bold: ${tokens("fontWeight.semibold")}`,

    `--au-transition-fast: ${tokens("transitionDuration.200")}`,

    `--au-icon-width-sm: ${tokens("spacing.md")};`,
    `--au-icon-width-md: ${tokens("spacing.2md")};`,
    `--au-icon-width-lg: ${tokens("spacing.lg")};`,
  ].join(";")
}
